import { Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {AppSettings} from "../../../../conf/appSettings";

/** ===== Shared Types ===== */
export type InputType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'select'
  | 'boolean'
  | 'tel';

export interface FieldValidators {
  required?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  step?: number; // UI hint
}

export interface FieldConfig {
  key: string;
  label: string;
  type: InputType;
  required?: boolean;
  placeholder?: string;
  unit?: string; // e.g. ₪
  options_source?: string; // for select datasets
  validators?: FieldValidators;
  default?: any;
  visible?: boolean; // default = true
  help?: string;
}

export interface PaymentType {
  id: string;
  label: string;
  header_hint?: string;
  icon?: string;
  fields?: FieldConfig[];
  inherits?: string; // base payment type id
  overrides?: {
    fields?: Array<Partial<FieldConfig> & { key: string }>;
  };
}

export interface InvoiceConfig {
  version: string;
  entity: string;
  currency_default: string;
  ui: {
    allow_multiple_payments: boolean;
    allow_clone_payment: boolean;
    max_payments: number | null;
    show_section_titles: boolean;
  };
  base_fields: FieldConfig[];
  payments: {
    allow_mixed_types: boolean;
    types: PaymentType[];
  };
  datasets: Record<string, Array<{ value: string; label: string }>>;
}

/** ===== Service ===== */
@Injectable({ providedIn: 'root' })
export class InvoiceConfigService {
  constructor(private fb: FormBuilder, private settings:AppSettings) {}
  /** Hardcoded config (replace later) */
  private CONFIG: InvoiceConfig;

  /** Public API */
  getConfig(): InvoiceConfig {
    let settings = this.settings.getSettings('invoices') || {};
    this.CONFIG = settings['new_invoice_config'] as InvoiceConfig;

    return this.CONFIG;
  }
  getBaseFields(): FieldConfig[] {
    return this.CONFIG.base_fields;
  }
  getPaymentTypes(): PaymentType[] {
    return this.CONFIG.payments.types.map((t) => this.resolvePaymentType(t.id)!);
  }
  getPaymentTypeById(id: string): PaymentType | undefined {
    return this.resolvePaymentType(id);
  }
  getDataset(name: string) {
    return this.CONFIG.datasets[name] || [];
  }

  /** Build top-level base form (safe validators) */
  buildBaseFormGroup(): FormGroup {
    return this.buildFormGroupForFields(this.getBaseFields());
  }

  /** Build a FormGroup from FieldConfig[] (always returns valid validators array) */
  buildFormGroupForFields(fields: FieldConfig[]): FormGroup {
    const group: Record<string, FormControl> = {};
    fields
      .filter((f) => f.visible !== false)
      .forEach((f) => {
        const validators = this.buildValidators(f);
        group[f.key] = this.fb.control(f.default ?? null, validators); // always array
      });
    return this.fb.group(group);
  }

  /** Build a payment form by type id */
  buildPaymentFormByTypeId(typeId: string): FormGroup {
    const paymentType = this.getPaymentTypeById(typeId);
    if (!paymentType) throw new Error(`Unknown payment type: ${typeId}`);
    const fg = this.buildFormGroupForFields(paymentType.fields || []);
    fg.addControl('type', this.fb.control(typeId, [Validators.required]));
    return fg;
  }

  /** Build payload safely */
  buildPayload(form: FormGroup) {
    const currency = this.CONFIG.currency_default;
    const base: Record<string, any> = { entity: this.CONFIG.entity, currency };

    this.CONFIG.base_fields.forEach((f) => {
      if (f.visible === false) return;
      base[f.key] = form.get(f.key)?.value ?? null;
    });

    const paymentsFA = form.get('payments') as FormArray | null;
    const payments = (paymentsFA?.controls || []).map((ctrl) => {
      const g = ctrl as FormGroup;
      const typeId = g.get('type')?.value;
      const type = this.getPaymentTypeById(typeId);
      const out: Record<string, any> = { type: typeId };
      (type?.fields || [])
        .filter((f) => f.visible !== false)
        .forEach((f) => (out[f.key] = g.get(f.key)?.value ?? null));
      return out;
    });

    const total = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    return { ...base, payments, total };
  }

  /** ===== Validators (PUBLIC, always returns array) ===== */
  public buildValidators(field: FieldConfig): ValidatorFn[] {
    const v = field.validators || {};
    const list: ValidatorFn[] = [];
    if (field.required) list.push(Validators.required);
    if (v.min !== undefined) list.push(Validators.min(v.min));
    if (v.max !== undefined) list.push(Validators.max(v.max));
    if (v.minLength !== undefined) list.push(Validators.minLength(v.minLength));
    if (v.maxLength !== undefined) list.push(Validators.maxLength(v.maxLength));
    if (v.pattern) list.push(Validators.pattern(v.pattern));
    return list; // never null/undefined
  }

  /** ===== inheritance + overrides ===== */
  private resolvePaymentType(id: string): PaymentType | undefined {
    const base = this.CONFIG.payments.types.find((t) => t.id === id);
    if (!base) return undefined;

    if (!base.inherits) {
      return {
        ...base,
        fields: (base.fields || []).map((f) => ({ visible: true, ...f })),
      };
    }

    const parent = this.resolvePaymentType(base.inherits);
    if (!parent)
      throw new Error(`inherits refers to unknown type: ${base.inherits}`);

    const parentFields = [...(parent.fields || [])];
    const ownFields = [...(base.fields || [])];
    const merged = new Map<string, FieldConfig>();

    [...parentFields, ...ownFields].forEach((f) =>
      merged.set(f.key, { visible: true, ...f })
    );

    if (base.overrides?.fields) {
      base.overrides.fields.forEach((ov) => {
        const curr = merged.get(ov.key);
        if (curr) merged.set(ov.key, { ...curr, ...ov });
      });
    }
    return { ...base, fields: Array.from(merged.values()) };
  }

  getPaymentHeaderHintById(id: string): string | null {
      const t = this.getPaymentTypeById(id);
      return (t as any)?.header_hint || null;
    }
}